import { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { InfoTooltip } from "../../../components";
import type {
  DispatchSummary,
  DailyDispatchPoint,
} from "../../load-portfolio/domain/types";
import { SOURCE_TYPE_COLORS } from "../../load-portfolio/domain/types";

interface Props {
  dispatch: DispatchSummary;
  daily: DailyDispatchPoint[];
}

type View = "profile" | "mix";

const VIEWS: { value: View; label: string }[] = [
  { value: "profile", label: "Annual Profile" },
  { value: "mix", label: "Mix & Coverage" },
];

const GRID_COLOR = "#3b82f6"; // blue — grid imports serving the load
const UNSERVED_COLOR = "#f97316"; // orange — true shortfall (islanded only)

function formatMWh(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)} TWh`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)} GWh`;
  return `${val.toFixed(0)} MWh`;
}

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function dayToMonth(day: number): string {
  let cum = 0;
  for (let i = 0; i < 12; i++) {
    cum += MONTH_DAYS[i];
    if (day <= cum) return MONTH_LABELS[i];
  }
  return "Dec";
}

function monthTicks(): number[] {
  const ticks: number[] = [];
  let cum = 0;
  for (const d of MONTH_DAYS) {
    ticks.push(cum + 1);
    cum += d;
  }
  return ticks;
}

function BarSegment({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  if (pct < 0.5) return null;
  return (
    <div
      className="relative h-10 flex items-center justify-center text-body-sm font-medium text-white"
      style={{
        width: `${pct}%`,
        backgroundColor: color,
        minWidth: pct > 3 ? undefined : "24px",
      }}
      title={`${label}: ${formatMWh(value)} (${pct.toFixed(1)}%)`}
    >
      {pct > 8 && (
        <span className="truncate px-1">
          {label} {pct.toFixed(0)}%
        </span>
      )}
    </div>
  );
}

function ProfileTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-body-sm shadow-lg"
      style={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
    >
      <div className="text-text-muted mb-1">
        Day {label} ({dayToMonth(label)})
      </div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="text-text-primary font-medium">
            {p.value.toFixed(1)} MW avg
          </span>
        </div>
      ))}
    </div>
  );
}

function ProfileLegend({ payload }: any) {
  if (!payload) return null;
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-2 text-body-sm">
      {payload.map((entry: any) => (
        <div key={entry.value} className="flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-text-muted">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Unified card combining the annual dispatch profile + mix/coverage stats
 * (formerly LoadGenerationChart + LoadDispatchChart). Two view tabs:
 *
 * - "Annual Profile": stacked-area dispatch over the year with the load
 *   curve overlaid as a dashed line.
 * - "Mix & Coverage": single-row mix bar + the eight key stats.
 *
 * Important correction over the older cards: for grid-tied loads (no
 * meaningful BTM RE/BESS/NG), the residual "Unserved" segment is the
 * grid serving the load — relabel it "Grid" (blue) and drop the
 * "consider adding capacity" warning. Only treat it as a real shortfall
 * (orange) when BTM resources exist and there's a true islanded gap.
 */
export function LoadServingStatsCard({ dispatch, daily }: Props) {
  const [view, setView] = useState<View>("profile");

  const total = dispatch.total_load_mwh || 0;
  const btmServed =
    (dispatch.total_re_served_mwh || 0) +
    (dispatch.total_bess_discharged_mwh || 0) +
    (dispatch.total_ng_gen_mwh || 0);
  // Grid-tied = BTM resources cover <5% of load. The model's "unserved"
  // bucket for these loads is really the grid serving them.
  const isGridTied = total > 0 ? btmServed / total < 0.05 : true;
  const residualMwh = dispatch.total_unserved_mwh ?? 0;
  const residualLabel = isGridTied ? "Grid" : "Unserved";
  const residualColor = isGridTied ? GRID_COLOR : UNSERVED_COLOR;

  // Profile chart data — relabel the `unserved` series to "Grid" for
  // grid-tied loads. The underlying daily key stays `unserved` (engine
  // schema) but the displayed series name flips.
  const areas = useMemo(
    () => [
      { key: "re_direct", name: "RE Direct", color: SOURCE_TYPE_COLORS.solar },
      { key: "bess", name: "BESS", color: SOURCE_TYPE_COLORS.bess },
      { key: "ng", name: "Natural Gas", color: SOURCE_TYPE_COLORS.ng },
      { key: "curtailed", name: "Curtailed", color: "#6b7280" },
      { key: "unserved", name: residualLabel, color: residualColor },
    ],
    [residualLabel, residualColor],
  );

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      <div className="px-6 pt-5 pb-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-text-primary">
            Load Serving Stats
          </h2>
          <p className="text-xs text-text-muted mt-0.5">
            How the load is served hour-by-hour over the scenario year.
            {isGridTied
              ? " This is a grid-tied load: the blue 'Grid' segment is the marginal grid dispatch (fossil-heavy by construction of the consequential model)."
              : " This load is partly islanded; the orange 'Unserved' segment is true demand the BTM stack couldn't cover."}
          </p>
        </div>
        <div className="flex gap-1 text-xs shrink-0">
          {VIEWS.map((v) => (
            <button
              key={v.value}
              onClick={() => setView(v.value)}
              className={`px-3 py-1 rounded ${
                view === v.value
                  ? "bg-quantum-500/20 text-quantum-300 border border-quantum-500/40"
                  : "bg-surface-600 text-text-secondary hover:text-text-primary border border-transparent"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === "profile" && daily && daily.length > 0 && (
        <div className="px-4 pb-4">
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart
              data={daily}
              margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
            >
              <defs>
                {areas.map(({ key, color }) => (
                  <linearGradient
                    key={key}
                    id={`load_stats_grad_${key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={color} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis
                dataKey="day"
                ticks={monthTicks()}
                tickFormatter={(d) => dayToMonth(d)}
                stroke="#94a3b8"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                stroke="#94a3b8"
                tick={{ fontSize: 12 }}
                label={{
                  value: "MW (24hr Avg)",
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: "#e2e8f0", fontSize: 14, fontWeight: 600 },
                }}
              />
              <RechartsTooltip content={<ProfileTooltip />} />
              <Legend content={<ProfileLegend />} />
              {areas.map(({ key, name, color }) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={name}
                  stackId="1"
                  stroke={color}
                  fill={`url(#load_stats_grad_${key})`}
                  strokeWidth={0}
                />
              ))}
              <Line
                type="monotone"
                dataKey="load"
                name="Load"
                stroke="#ffffff"
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === "mix" && (
        <div className="px-6 pb-5 space-y-4">
          {/* Single-row stacked bar */}
          <div className="flex rounded-lg overflow-hidden">
            <BarSegment
              label="Solar+Wind"
              value={dispatch.total_re_served_mwh}
              total={total}
              color={SOURCE_TYPE_COLORS.solar}
            />
            <BarSegment
              label="BESS"
              value={dispatch.total_bess_discharged_mwh}
              total={total}
              color={SOURCE_TYPE_COLORS.bess}
            />
            <BarSegment
              label="NG"
              value={dispatch.total_ng_gen_mwh}
              total={total}
              color={SOURCE_TYPE_COLORS.ng}
            />
            {residualMwh > 0 && (
              <BarSegment
                label={residualLabel}
                value={residualMwh}
                total={total}
                color={residualColor}
              />
            )}
          </div>

          {/* True-shortfall warning — only for partially-islanded loads */}
          {!isGridTied && residualMwh > 0 && (
            <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-3">
              <svg
                className="w-5 h-5 text-orange-400 mt-0.5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-body-sm text-orange-300">
                {dispatch.hours_unserved} hours of unserved load (
                {formatMWh(residualMwh)}, peak{" "}
                {dispatch.max_unserved_mw?.toFixed(1)} MW). Consider adding
                capacity.
              </span>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-body-sm">
            <div>
              <span className="text-text-muted">Total Load</span>
              <div className="text-text-primary font-medium">
                {formatMWh(dispatch.total_load_mwh)}
              </div>
            </div>
            <div>
              <span className="text-text-muted">
                RE Coverage{" "}
                <InfoTooltip
                  text="Percentage of total load served by BTM RE (direct) + BESS discharge."
                  position="bottom"
                />
              </span>
              <div className="text-quantum-400 font-medium">
                {dispatch.re_coverage_pct.toFixed(1)}%
              </div>
            </div>
            <div>
              <span className="text-text-muted">
                Grid Share{" "}
                <InfoTooltip
                  text="Percentage of total load served by the grid (marginal dispatch from the host BA)."
                  position="bottom"
                />
              </span>
              <div className="text-text-primary font-medium">
                {total > 0 ? ((residualMwh / total) * 100).toFixed(1) : "0.0"}%
              </div>
            </div>
            <div>
              <span className="text-text-muted">
                NG Capacity Factor{" "}
                <InfoTooltip
                  text="BTM NG generation as a fraction of maximum possible output (capacity x 8760 hours)."
                  position="bottom"
                />
              </span>
              <div className="text-text-primary font-medium">
                {(dispatch.ng_capacity_factor * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <span className="text-text-muted">
                BESS Cycles{" "}
                <InfoTooltip
                  text="Total energy discharged divided by battery capacity. Higher cycles = more utilization."
                  position="bottom"
                />
              </span>
              <div className="text-text-primary font-medium">
                {dispatch.bess_cycles.toFixed(0)}
              </div>
            </div>
            <div>
              <span className="text-text-muted">RE Generated</span>
              <div className="text-text-primary font-medium">
                {formatMWh(dispatch.total_re_gen_mwh)}
              </div>
            </div>
            <div>
              <span className="text-text-muted">RE to Load</span>
              <div className="text-text-primary font-medium">
                {formatMWh(dispatch.total_re_served_mwh)}
              </div>
            </div>
            <div>
              <span className="text-text-muted">Curtailed</span>
              <div className="text-text-primary font-medium">
                {formatMWh(dispatch.total_curtailed_mwh)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
